import { isMeinBuero } from '@/utils/build-env'

export const GtmEventTypes = {
  MboOnboarding: 'mbo-onboarding',
  GaEventHub: 'gaEventHub',
  AddToCart: 'add_to_cart',
  RemoveFromCart: 'remove_from_cart',
  Purchase: 'purchase',
  BeginCheckout: 'begin_checkout',
  PackagePurchase: 'packagePurchase',
  RegistrationRequested: 'registrierung-angefordert',
  RegistrationApproved: 'registrierung-bestaetigt',
  MboDownload: 'mbo-download',
  OnboardingFinished: 'onboarding-finished',
} as const

export type EventType = typeof GtmEventTypes[keyof typeof GtmEventTypes]

interface BaseEvent {
  event: EventType
}

export interface IGenericEventData extends BaseEvent {
  // eslint-disable-next-line
  [key: string]: any
}

export interface IMboOnboardingEventData extends BaseEvent {
  event: typeof GtmEventTypes.MboOnboarding
  'mbo-onboarding-step'?: string
  PageURL?: string
}
interface IMboEcommerceProduct {
  name: string
  id: string
  price: number
  brand: string
  category: string
  variant: string,
  quantity: string
}
export interface IMboShopEventData extends BaseEvent {
  event: typeof GtmEventTypes.PackagePurchase
  Zahlungsart: string,
  ecommerce: {
    purchase: {
      actionField: {
        id: string,
        affiliation: string,
        revenue: number,
        tax: number,
        shipping: string,
      },
      products: IMboEcommerceProduct[],
    },
  },
}

export interface IGaEventData extends BaseEvent {
  event: typeof GtmEventTypes.GaEventHub
  eventHubCategory?: string
  eventHubAction?: string
  eventHubLabel?: string
  eventCategory?: string
  eventAction?: string
  eventLabel?: string
  email?: string
}
interface IEcommerceItem {
  item_name?: string
  index?: number
  item_brand?: string
  item_category?: string
  price?: number
  quantity?: number
  item_id?: string
  affiliation?: string
  item_variant?: string
}
export interface IShopEventData extends BaseEvent {
  event:
    | typeof GtmEventTypes.AddToCart
    | typeof GtmEventTypes.RemoveFromCart
    | typeof GtmEventTypes.Purchase
    | typeof GtmEventTypes.BeginCheckout
  Zahlungsart?: string
  ecommerce: {
    removeCompleteItem?: boolean
    currency: string
    value: number
    items: IEcommerceItem[]
    transaction_id?: string,
    tax?: number,
    shipping?: string
  }
}

export type GTMEvent =
  | IGaEventData
  | IMboOnboardingEventData
  | IShopEventData
  | IGenericEventData

const defaultMboOnboardingData: IMboOnboardingEventData = {
  event: undefined,
  'mbo-onboarding-step': undefined,
  PageURL: undefined,
}
const defaultMboShopEventData: IMboShopEventData = {
  event: undefined,
  Zahlungsart: undefined,
  ecommerce: {
    purchase: undefined,
  },
}

const defaultGaEventData: IGaEventData = {
  event: undefined,
  eventHubCategory: undefined,
  eventHubAction: undefined,
  eventHubLabel: undefined,
  eventCategory: undefined,
  eventAction: undefined,
  eventLabel: undefined,
  email: undefined,
}
const defaultShopEventData: IShopEventData = {
  event: undefined,
  Zahlungsart: undefined,
  ecommerce: {
    removeCompleteItem: undefined,
    currency: undefined,
    value: undefined,
    items: undefined,
    transaction_id: undefined,
    tax: undefined,
    shipping: undefined,
  },
}

const isMB = isMeinBuero()
const GtmService = {
  pushEvent(event: GTMEvent): void {
    window.dataLayer = window.dataLayer || []

    if (!event.event) {
      console.error('GTM pushEvent: no event defined!')
      return
    }

    // FYI: Because of the functionality of the dataLayer we explicitly need to set the data we don't want to send to undefined
    // https://developers.google.com/tag-platform/tag-manager/datalayer?hl=de#how_data_layer_information_is_processed
    let eventData
    if (isMB) {
      eventData = {
        ...defaultMboOnboardingData,
        ...defaultMboShopEventData,
        ...defaultGaEventData,
        ...event,
      }
    } else {
      eventData = {
        ...defaultShopEventData,
        ...defaultGaEventData,
        ...defaultShopEventData,
        ...event,
      }

    }

    if (!('ecommerce' in event)) {
      eventData.ecommerce = undefined
    }

    window.dataLayer.push(eventData)
  },
}

export default GtmService
